import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import store, { State as RootState } from '@/store'

import {UserProfile, UserProfileItem} from "@/types/UserType";
import {
    createProfile,
    fetchProfile,
    fetchProfileItems,
    sortItems,
    uploadFile,
    deleteItem,
    sortGroupsItems,
    deleteCover
} from "@/data/requests/profiles";

// Declare state
export type State = {
    profile: UserProfile | null,
    items: UserProfileItem[],
    groups: Record<string, UserProfileItem[]>,
}

// Mutation contracts
export type Mutations<S = State> = {
    setProfile(state: S, { profile }: { profile: UserProfile }): void,
    setProfileItems(state: S, { items }: { items: UserProfileItem[] }): void,
    setProfileItemsGrouped(state: S, { groups }: { groups: Record<string, UserProfileItem[]> }): void,
    addGroup(state: S, { name }: { name: string }): void,
    renameGroup(state: S, { from, to }: { from: string, to: string }): void,
}

// Actions context
type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

// Actions contracts
export interface Actions {
    loadProfile(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    createProfile(
        { commit }: AugmentedActionContext,
        payload: { title: string }
    ): Promise<any>,
    loadProfileItems(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    sortItems(
        { commit }: AugmentedActionContext,
        payload: { items: UserProfileItem[] }
    ): Promise<any>,
    sortGroupedItems(
        { commit }: AugmentedActionContext,
        payload: { groups: Record<string, UserProfileItem[]> }
    ): Promise<any>,
    uploadFile(
        { commit }: AugmentedActionContext,
        payload: { file: File }
    ): Promise<any>,
    deleteItem(
        { commit }: AugmentedActionContext,
        payload: { item: UserProfileItem }
    ): Promise<any>,
    deleteCover(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    addGroup(
        { commit }: AugmentedActionContext,
        payload: { name: string }
    ): Promise<any>,
    renameGroup(
        { commit }: AugmentedActionContext,
        payload: { from: string, to: string }
    ): Promise<any>,
}

// Getters types
export type Getters = {
    profile(state: State): UserProfile | null,
    items(state: State): UserProfileItem[],
    itemsGrouped(state: State): Record<string, UserProfileItem[]>,
}

const state: State = {
    profile: null,
    items: [],
    groups: {},
};

const getters: GetterTree<State, RootState> & Getters = {
    profile: state => state.profile,
    items: state => state.items,
    itemsGrouped: state => state.groups,
};

const mutations: MutationTree<State> & Mutations = {
    setProfile(state, { profile }) {
        state.profile = profile;
    },
    setProfileItems(state, { items }) {
        state.items = items;
    },
    setProfileItemsGrouped(state, {groups}) {
        state.groups = groups || {};

        const flattened: UserProfileItem[] = Object.values(state.groups).flat();

        if(flattened.length) {
            state.items = flattened;
        }
    },
    addGroup(state, { name }) {
        if(!name || state.groups[name]) return;

        state.groups = { ...state.groups, [name]: [] };
    },
    renameGroup(state, {from, to}) {
        if(!state.groups[from] || !to || from === to || state.groups[to]) return;

        const out: Record<string, UserProfileItem[]> = {};
        for (const [k, v] of Object.entries(state.groups)) {
            out[k === from ? to : k] = v;
        }
        state.groups = out;
        state.items = Object.values(state.groups).flat();
    }
};

const actions: ActionTree<State, RootState> & Actions = {
    loadProfile({commit}: AugmentedActionContext, payload: {}) {
        return new Promise((resolve: any, reject) => {
            fetchProfile((profile) => {
                commit('setProfile', {profile});

                resolve(profile);
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    createProfile({commit}: AugmentedActionContext, payload: {title: string}) {
        return new Promise((resolve: any, reject) => {
            createProfile(payload.title, (profile) => {
                commit('setProfile', {profile});

                resolve(profile);
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    loadProfileItems({commit}: AugmentedActionContext) {
        return new Promise((resolve: any, reject) => {
            fetchProfileItems((items) => {
                if(Array.isArray(items)) {
                    commit('setProfileItems', {items});

                    commit('setProfileItemsGrouped', { groups: {} })

                    resolve(items);
                } else if (items && typeof items === 'object') {
                    commit('setProfileItems', {items: [] });

                    commit('setProfileItemsGrouped', { groups: items })

                    resolve(items);
                } else {
                    commit('setProfileItems', {items: [] });
                    commit('setProfileItemsGrouped', { groups: {} })
                    resolve([]);
                }
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    sortItems({commit, getters}, {items}): Promise<any> {
        const tempItems = getters.items;

        commit('setProfileItems', {items: (items.length === 0 ? tempItems : items)});

        return new Promise((resolve: any, reject) => {
            sortItems(items, () => {
                resolve();
            }, (type, data) => {
                commit('setProfileItems', {items: tempItems});

                reject({type, data});
            })
        })
    },


    sortGroupedItems({commit, getters}, { groups }): Promise<any> {
        const tempGroups = getters.itemsGrouped;

        commit('setProfileItemsGrouped', {groups: (Object.keys(groups || {}).length === 0 ? tempGroups : groups)});

        return new Promise((resolve: any, reject) => {
            sortGroupsItems(groups, () => {
                resolve();
            }, (type, data) => {
                commit('setProfileItemsGrouped', {groups: tempGroups});

                reject({type, data});
            })
        })
    },

    async uploadFile({commit, dispatch}, { file, group }: { file: File; group?: string }): Promise<any> {
        return await new Promise((resolve: any, reject) => {
            uploadFile(file, group, (item) => {
                dispatch('loadProfileItems');

                resolve(item);
            }, (type, data) => {
                reject({type, data});
            })
        });
    },

    deleteItem({commit, getters}, {item}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deleteItem(item, () => {
                commit('setProfileItems', {
                    items: getters.items.filter((_item: UserProfileItem) => {
                        return _item.id != item.id;
                    })
                })

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        })
    },

    deleteCover({commit}: AugmentedActionContext, payload: {}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            deleteCover(() => {
                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        })
    },

    addGroup({commit}, {name}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            commit('addGroup', {name});

            resolve();
        })
    },

    renameGroup({commit}, {from, to}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            commit('renameGroup', {from, to});

            resolve();
        })
    },
};

//setup store type
export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const ProfileModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}